import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectActorZoneChangeCount,
  selectPortfolioZoneChangeCount,
  selectCharacterZoneChangeCount,
  selectVariationFormChangeCount,
  selectVariationListChangeCount,
  selectVariationVersionsListChangeCount
} from '../slices/voiceActorDataSlice';

/**
 * 🚀 VOICE ACTOR STABLE HOOKS
 * 
 * Stable wrapper hooks that prevent unnecessary re-renders by creating stable references
 * Adapted from BookWorksheet stable hooks for VoiceActor entity hierarchy
 * 
 * Entity Mapping:
 * BookWorksheet → VoiceActor
 * litGroupId   → actorId
 * bookId       → portfolioId
 * characterId  → characterId (same)
 * variationId  → variationId (same)
 */

/**
 * 🚀 ZONE-BASED: Stable wrapper for useVariationManagement - only updates on Variation Zone changes
 * This prevents unnecessary re-renders by creating stable references
 */
export const useStableVariationManagement = () => {
  // 🚀 ZONE-BASED: Watch Variation Zone changes
  const variationListChangeCount = useSelector(selectVariationListChangeCount);
  const variationFormChangeCount = useSelector(selectVariationFormChangeCount);

  // Import the hook dynamically to avoid circular dependencies
  const { useVariationZone } = require('./core/useZoneHooks');
  const variationManagement = useVariationZone();

  return useMemo(() => {
    if (process.env.NODE_ENV === 'development' && window.DEBUG_MEMOIZATION) {
      console.log('[VA_ZONE_STABLE_HOOK] useStableVariationManagement updated, listCount:', variationListChangeCount, 'formCount:', variationFormChangeCount);
    }

    // Return the variation management data - this will only re-compute when Variation Zone changes
    return variationManagement;
  }, [variationManagement, variationListChangeCount, variationFormChangeCount]); // 🚀 ZONE-BASED: Only Variation Zone changes!
};

/**
 * 🚀 ZONE-BASED: Stable wrapper for useSharedData - only updates on shared data changes
 * This prevents unnecessary re-renders by creating stable references
 */
export const useStableSharedData = () => {
  // 🚀 ZONE-BASED: Watch shared data changes (actors, portfolios, selections)
  const { useSharedData } = require('./core/useZoneHooks');
  const sharedData = useSharedData();

  // Create a change counter based on shared data state
  const sharedDataChangeCount = useMemo(() => {
    return (sharedData.lastUpdated?.actors || 0) +
      (sharedData.lastUpdated?.portfolios || 0) +
      (sharedData.selections?.actorId ? 1 : 0) +
      (sharedData.selections?.portfolioId ? 1 : 0) +
      (sharedData.selections?.characterId ? 1 : 0);
  }, [
    sharedData.lastUpdated?.actors,
    sharedData.lastUpdated?.portfolios,
    sharedData.selections?.actorId,
    sharedData.selections?.portfolioId,
    sharedData.selections?.characterId
  ]);

  return useMemo(() => {
    if (process.env.NODE_ENV === 'development' && window.DEBUG_MEMOIZATION) {
      console.log('[VA_ZONE_STABLE_HOOK] useStableSharedData updated, changeCount:', sharedDataChangeCount);
    }

    // Return the shared data - this will only re-compute when shared data changes
    return sharedData;
  }, [sharedData, sharedDataChangeCount]); // 🚀 ZONE-BASED: Only shared data changes!
};

/**
 * 🚀 ZONE-BASED: Stable wrapper for useCharacterZone - only updates on Character Zone changes
 * This prevents unnecessary re-renders by creating stable references
 */
export const useStableCharacterZone = () => {
  // 🚀 ZONE-BASED: Watch Character Zone changes
  const characterZoneChangeCount = useSelector(selectCharacterZoneChangeCount);

  // Import the hook dynamically to avoid circular dependencies
  const { useCharacterZone } = require('./core/useZoneHooks');
  const characterZone = useCharacterZone();

  return useMemo(() => {
    if (process.env.NODE_ENV === 'development' && window.DEBUG_MEMOIZATION) {
      console.log('[VA_ZONE_STABLE_HOOK] useStableCharacterZone updated, changeCount:', characterZoneChangeCount);
    }

    // Return the character zone data - this will only re-compute when Character Zone changes
    return characterZone;
  }, [characterZone, characterZoneChangeCount]); // 🚀 ZONE-BASED: Only Character Zone changes!
};

/**
 * 🚀 ZONE-BASED: Stable wrapper for useActorZone - only updates on Actor Zone changes
 * This prevents unnecessary re-renders by creating stable references
 */
export const useStableActorZone = () => {
  // 🚀 ZONE-BASED: Watch Actor Zone changes
  const actorZoneChangeCount = useSelector(selectActorZoneChangeCount);

  // Import the hook dynamically to avoid circular dependencies
  const { useActorZone } = require('./core/useZoneHooks');
  const actorZone = useActorZone();

  return useMemo(() => {
    if (process.env.NODE_ENV === 'development' && window.DEBUG_MEMOIZATION) {
      console.log('[VA_ZONE_STABLE_HOOK] useStableActorZone updated, changeCount:', actorZoneChangeCount);
    }

    // Return the actor zone data - this will only re-compute when Actor Zone changes
    return actorZone;
  }, [actorZone, actorZoneChangeCount]); // 🚀 ZONE-BASED: Only Actor Zone changes!
};

/**
 * 🚀 ZONE-BASED: Stable wrapper for usePortfolioZone - only updates on Portfolio Zone changes
 * This prevents unnecessary re-renders by creating stable references
 */
export const useStablePortfolioZone = () => {
  // 🚀 ZONE-BASED: Watch Portfolio Zone changes
  const portfolioZoneChangeCount = useSelector(selectPortfolioZoneChangeCount);

  // Import the hook dynamically to avoid circular dependencies
  const { usePortfolioZone } = require('./core/useZoneHooks');
  const portfolioZone = usePortfolioZone();

  return useMemo(() => {
    if (process.env.NODE_ENV === 'development' && window.DEBUG_MEMOIZATION) {
      console.log('[VA_ZONE_STABLE_HOOK] useStablePortfolioZone updated, changeCount:', portfolioZoneChangeCount);
    }

    // Return the portfolio zone data - this will only re-compute when Portfolio Zone changes
    return portfolioZone;
  }, [portfolioZone, portfolioZoneChangeCount]); // 🚀 ZONE-BASED: Only Portfolio Zone changes!
};

/**
 * 🚀 ZONE-BASED: Stable wrapper for useVariationList - only updates on Variation Zone list changes
 * This prevents unnecessary re-renders by creating stable references
 */
export const useStableVariationList = () => {
  // 🚀 ZONE-BASED: Only watch Variation Zone list changes
  const variationListChangeCount = useSelector(selectVariationListChangeCount);

  // Import the hook dynamically to avoid circular dependencies
  const { useVariationZone } = require('./core/useZoneHooks');
  const variationZone = useVariationZone();

  return useMemo(() => {
    if (process.env.NODE_ENV === 'development' && window.DEBUG_MEMOIZATION) {
      console.log('[VA_ZONE_STABLE_HOOK] useStableVariationList updated, changeCount:', variationListChangeCount);
    }

    // Return the variation list data - this will only re-compute when Variation Zone list changes
    return variationZone;
  }, [variationZone, variationListChangeCount]); // 🚀 ZONE-BASED: Only Variation Zone list changes!
};

/**
 * 🚀 ZONE-BASED: Stable wrapper for useVariationVersions - only updates on Variation Zone version changes
 * This prevents unnecessary re-renders by creating stable references
 */
export const useStableVariationVersions = () => {
  // 🚀 ZONE-BASED: Only watch Variation Zone version changes
  const versionsListChangeCount = useSelector(selectVariationVersionsListChangeCount);

  // Import the hook dynamically to avoid circular dependencies
  const { useVariationZone } = require('./core/useZoneHooks');
  const variationZone = useVariationZone();

  return useMemo(() => {
    if (process.env.NODE_ENV === 'development' && window.DEBUG_MEMOIZATION) {
      console.log('[VA_ZONE_STABLE_HOOK] useStableVariationVersions updated, changeCount:', versionsListChangeCount);
    }

    // Return the variation version data - this will only re-compute when Variation Zone version changes
    return variationZone;
  }, [variationZone, versionsListChangeCount]); // 🚀 ZONE-BASED: Only Variation Zone version changes!
};

/**
 * 🚀 ZONE-BASED: Stable wrapper for useEditVersionManager - only updates on Variation Zone form editing
 * This prevents unnecessary re-renders by creating stable references
 * ENHANCED: Now includes activeVersion data in dependencies for proper form sync
 */
export const useStableEditVersionManager = () => {
  // 🚀 ZONE-BASED: Only watch Variation Zone form editing
  const formChangeCount = useSelector(selectVariationFormChangeCount);

  // Import the hook dynamically to avoid circular dependencies
  const useEditVersionManager = require('./useEditVersionManager_upgrade').default;
  const editVersionManager = useEditVersionManager();
  const activeVersionUuid = editVersionManager.activeVersion?.uuid;

  return useMemo(() => {
    if (process.env.NODE_ENV === 'development' && window.DEBUG_MEMOIZATION) {
      console.log('[VA_ZONE_STABLE_HOOK] useStableEditVersionManager updated, changeCount:', formChangeCount, 'activeVersion:', {
        uuid: activeVersionUuid
      });
    }

    // Return the manager data - this will only re-compute when Variation Zone form editing changes OR activeVersion changes
    return editVersionManager;
  }, [editVersionManager, formChangeCount, activeVersionUuid]); // 🚀 PERFORMANCE: Depend on formChangeCount and activeVersion for proper sync
};
